import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { FavouriteButton } from 'widgets/player-awards-tracker/js/common/FavouriteButton';
import { StatsTableRowHeader } from 'widgets/statspro/js/components/common/stats-table/StatsTableRowHeader';
import { getTranslation } from 'common/react/utils/translations';
import { PlayerPhoto } from 'common/react/components/PlayerPhoto';
import { useTeamsList, getIsAFLW } from '../utils';
import { navSelector } from 'common/store/modules/competition-nav/selectors';
import { leaderboardSelector } from 'common/store/modules/player-awards/leaderboard/selectors/simple-selectors';
import { useActivatePlayerModal } from '../../../hooks/use-activate-player-modal';

export const PlayerCell = ({
    player,
    onPlayerFavouriteClick,
    favourites,
    seasonPid,
    shouldHidePredicted,
    isBrownlowV2,
    count,
    status,
    ...cellProps
}) => {
    const nav = useSelector(navSelector);
    const leaderboardState = useSelector(leaderboardSelector);

    const compAbbr = PULSE.app.common.match.getCompAbbr(seasonPid);
    const isAFLW = getIsAFLW(seasonPid);

    const teamsList = useTeamsList(nav.season.id);

    const teams = [
        { id: -1, name: getTranslation('label.stats.allClubs') },
        ...teamsList.sort((a, b) => a.name.localeCompare(b.name))
    ];

    const seasons = nav?.seasons;

    const activatePlayerModal = useActivatePlayerModal({
        player,
        seasonPid,
        nav,
        shouldHidePredicted,
        isBrownlowV2,
        isAFLW,
        count,
        getFiltersString: () =>
            composeFiltersString(nav, leaderboardState, teams, seasons)
    });

    return (
        <StatsTableRowHeader
            cellProps={cellProps}
            className="stats-table__row-header-cell"
        >
            <div className="stats-table__row-brownlow-player-cell">
                <FavouriteButton
                    player={player}
                    onPlayerFavouriteClick={onPlayerFavouriteClick}
                    favourites={favourites}
                    isAFLW={isAFLW}
                />

                <PlayerPhoto
                    className={
                        'stats-leaders-table-player__headshot u-hide-until-desktop'
                    }
                    photoUrl={player?.photoURL}
                    scale={'0.2'}
                    alt={`${getTranslation('label.stats.headshot.alt')} ${
                        player.givenName ?? ''
                    } ${player.surname ?? ''}`}
                />

                <div>
                    <button
                        onClick={(event) => activatePlayerModal(event)}
                        onKeyDown={(event) => activatePlayerModal(event)}
                        className={`stats-table__row-brownlow-player-name ${
                            !player.eligible
                                ? 'stats-table__row-brownlow-player-name-ineligible'
                                : ''
                        }`}
                    >
                        <p>{player.givenName}</p>
                        <p>{player.surname}</p>
                    </button>

                    {!player.eligible && (
                        <span className="stats-table__row-brownlow-ineligible">
                            {getTranslation(
                                'label.brownlow.predictor.ineligible'
                            )}
                        </span>
                    )}
                </div>

                {status &&
                    status ===
                        PULSE.app.common.CONSTANTS.MATCH_STATUS.CONCLUDED &&
                    player.winner && (
                        <img
                            className="stats-table__player-cell-brownlow-badge"
                            src={`/resources/${
                                window.RESOURCES_VERSION
                            }/i/elements/${
                                compAbbr === 'AFLW'
                                    ? 'bandf-medal-gold.svg'
                                    : 'brownlow-gold.svg'
                            }`}
                            alt={`${getTranslation(
                                'label.stat.brownlowMedal'
                            )}`}
                        />
                    )}
            </div>
        </StatsTableRowHeader>
    );
};

PlayerCell.propTypes = {
    player: PropTypes.object.isRequired,
    onPlayerFavouriteClick: PropTypes.func,
    favourites: PropTypes.array,
    seasonPid: PropTypes.string,
    shouldHidePredicted: PropTypes.bool,
    isBrownlowV2: PropTypes.bool,
    count: PropTypes.number,
    status: PropTypes.string
};

export const composeFiltersString = (nav, leaderboardState, teams, seasons) => {
    let season = {
        name: ''
    };
    let club = {
        name: ''
    };
    if (nav?.season) {
        seasons.length && nav.season.id
            ? (season = seasons.find((s) => s.id === nav.season.id))
            : {
                  name: ''
              };
    }
    if (leaderboardState?.team) {
        teams.length && leaderboardState.team.id
            ? (club = teams.find((s) => s.id === leaderboardState.team.id))
            : {
                  name: ''
              };
    }

    return `${season && `season: ${season.name}, `}${
        club && `club: ${club.name}`
    }`;
};
